import { useCallback, useContext, useEffect, useState } from "react";

import dayjs from "dayjs";
import _, { isEmpty } from "lodash";

import { useIsViewOnlyUser } from "manager/hooks/useIsViewOnlyUser";
import { useLegalAuditDocumentRedaction } from "manager/hooks/useLegalAuditDocumentRedaction";
import type { IncomeStream } from "renter/interfaces/api/income";
import Icon from "shared/components/Icon";
import {
  ConvertedAmount,
  shouldShowConversionDetails,
} from "shared/components/InternationalConversion";
import { IconLink } from "shared/components/Links";
import FraudSignalsIframeModal from "shared/components/Modals/FraudSignalsIframeModal";
import { CollapseButton } from "shared/components/Tile/styled";
import { RegularText } from "shared/components/Typography";
import { filterSuppressedFraudSignals } from "shared/config/fraudIndicators";
import {
  useGetOcrResultById,
  usePatchOcrResultById,
} from "shared/hooks/api/applicationQueries";
import useModal from "shared/hooks/useModal";
import { isManagerApp } from "shared/utils/auth";
import {
  editableStreamVerificationMethodType,
  getFirstValue,
  getInternationalFields,
  hasInternationalField,
  mapIncomeStreamVerificationMethodToType,
} from "shared/utils/employmentAndIncome";
import { useVeroFlag } from "shared/utils/featureFlags";
import { convertDollarsToCents } from "shared/utils/ui";
import { VeroFlag } from "shared/utils/veroFlags";

import ApplicationContext from "../../applicationContext";

import EditStreamDetailModal from "./EditStreamDetailModal";
import {
  detectSingleDocumentStream,
  formatStreamBankAccount,
  getAdaptiveDecisionReason,
  incomeIsFlaggedForFraud,
  ocrResultHasVisibleFraud,
  renderAmountCell,
  shouldCaptionAsConverted,
  streamHasFraud,
} from "./streamDetailHelpers";
import {
  ActionsContainer,
  DocumentLink,
  EditedChip,
  FraudCardSkeletonContainer,
  FraudCardSkeletonLine,
  FraudReasonLine,
  FraudSectionActions,
  FraudSectionContainer,
  FraudSectionLead,
  FraudSectionMeta,
  FraudSectionTitle,
  FraudSectionTitleGroup,
  FraudSectionTitleRow,
  FraudSectionTopRow,
  FraudSignalContainer,
  FraudSignalItem,
  FraudSignalList,
  StreamDetailContainer,
  IncomeStreamDetailTable,
  IncomeStreamHeader,
  StyledTooltip,
} from "./styled";

const FraudCardSkeleton = ({ compact = false }: { compact?: boolean }) => (
  <FraudCardSkeletonContainer $compact={compact}>
    <FraudCardSkeletonLine />
  </FraudCardSkeletonContainer>
);

const OcrRowPrefetcher = ({
  sourceOcrResult,
  onLoaded,
}: {
  sourceOcrResult: string;
  onLoaded: (id: string, ocrResult: any) => void;
}) => {
  const { ocrResult } = useGetOcrResultById(sourceOcrResult);
  useEffect(() => {
    if (ocrResult) onLoaded(sourceOcrResult, ocrResult);
  }, [ocrResult, sourceOcrResult, onLoaded]);
  return null;
};

// Exported for tests: both render sites below feed it the same ocrResult, so
// the card is the single place the reason line has to be verified.
export const FraudCard = ({
  ocrResult,
  dealDecisionIsMade,
  onOpenFraudViz,
  compact = false,
}: {
  ocrResult: any;
  dealDecisionIsMade: boolean;
  onOpenFraudViz: (ocrResult: any) => void;
  compact?: boolean;
}) => {
  const fraudSignals: any[] = filterSuppressedFraudSignals(
    _.get(ocrResult, "fraudSignals") || []
  );
  const adaptiveDecisionReason = getAdaptiveDecisionReason(ocrResult);
  const documentName = _.get(ocrResult, "documentName");
  const documentCreatedAt = _.get(ocrResult, "createdAt");
  const documentUrl = _.get(ocrResult, "url");
  const isVegaOcrResult = ocrResult?.provider === "vega";
  const hasFraudVisualization =
    !!ocrResult?.isFraudulent &&
    !!ocrResult?.fraudSubmissionId &&
    !!ocrResult?.url;
  const hasFraudVisualizationData = !_.isEmpty(
    ocrResult?.fraudVisualizations?.[0]?.visualization
  );
  const metaParts = _.compact([
    documentCreatedAt &&
      `Uploaded ${dayjs(documentCreatedAt).format("MM/DD/YYYY")}`,
  ]);

  return (
    <FraudSectionContainer $compact={compact}>
      <FraudSectionTopRow>
        <FraudSectionLead>
          <FraudSectionTitleGroup>
            <FraudSectionTitleRow>
              <FraudSectionTitle>Fraud Signals Detected</FraudSectionTitle>
            </FraudSectionTitleRow>
            {compact
              ? documentName && (
                  <FraudSectionMeta>{documentName}</FraudSectionMeta>
                )
              : (documentName || metaParts.length > 0) && (
                  <FraudSectionMeta>
                    {_.compact([documentName, ...metaParts]).join(" · ")}
                  </FraudSectionMeta>
                )}
          </FraudSectionTitleGroup>
        </FraudSectionLead>
        <FraudSectionActions>
          {hasFraudVisualization &&
            isVegaOcrResult &&
            hasFraudVisualizationData && (
              <DocumentLink
                size="small"
                type="link"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenFraudViz(ocrResult);
                }}
              >
                View
              </DocumentLink>
            )}
          {documentUrl && (
            <IconLink
              disabled={dealDecisionIsMade}
              Icon={Icon.DownloadDocumentIcon}
              href={documentUrl}
              target="_blank"
            />
          )}
        </FraudSectionActions>
      </FraudSectionTopRow>
      {!!adaptiveDecisionReason && (
        <FraudReasonLine $compact={compact}>
          <strong>Reason:</strong> {adaptiveDecisionReason}
        </FraudReasonLine>
      )}
      {!isEmpty(fraudSignals) && (
        <FraudSignalList $compact={compact}>
          {_.map(fraudSignals, (signal, idx) => (
            <FraudSignalItem
              $compact={compact}
              key={_.get(signal, "identifier") || idx}
            >
              {_.get(signal, "name") ||
                _.get(signal, "title") ||
                _.get(signal, "identifier")}
            </FraudSignalItem>
          ))}
        </FraudSignalList>
      )}
    </FraudSectionContainer>
  );
};

interface StreamDetailTableProps {
  incomeStream: IncomeStream;
  canViewOcrResults: boolean;
  canEditOcrResults: boolean;
}

export const StreamDetailTable = ({
  incomeStream,
  canViewOcrResults,
  canEditOcrResults,
}: StreamDetailTableProps) => {
  const { incomes, streamVerificationMethod } = incomeStream;
  const isTaxFormStream = [
    "ocr-1099",
    "ocr-w2",
    "ocr-international-tax-form",
  ].includes(streamVerificationMethod);
  const isEmploymentLetterStream =
    streamVerificationMethod === "ocr-international-employment-letter";
  const { application } = useContext(ApplicationContext);
  const { openModalDialog } = useModal();
  const { dealDecisionIsMade } = application || {};
  const { patchOcrResultById } = usePatchOcrResultById();
  const { isViewOnly } = useIsViewOnlyUser();
  const { shouldRedactDocuments } = useLegalAuditDocumentRedaction();
  const hasAnyDocumentUrl = incomeStream.incomes?.some(
    ({ documentUrl }) => !!documentUrl
  );

  const showInternationalIncome = useVeroFlag(
    VeroFlag.useInternationalDocumentUpload
  );
  const intlOcrFixesEnabled = useVeroFlag(VeroFlag.useIntlOcrFixes)
  const showConversionBreakdown =
    showInternationalIncome && intlOcrFixesEnabled;
  const displayIncomeStreamBankAccountEnabled = useVeroFlag(
    VeroFlag.displayIncomeStreamBankAccountProduct1957
  );
  const getIncomeBankAccount = (income: any): string | null =>
    formatStreamBankAccount(income) ?? formatStreamBankAccount(incomeStream);
  const renderBankAccount =
    isManagerApp() &&
    displayIncomeStreamBankAccountEnabled &&
    _.some(incomes, (income) => !!getIncomeBankAccount(income));

  const withConversionDetails = (
    income,
    annotationKey: "grossAmount" | "netAmount",
    originalAmountKey: "originalGrossAmount" | "originalNetAmount",
    convertedAmount: number | null | undefined,
    amount: JSX.Element
  ) => {
    const resolved = getInternationalFields({
      incomes: [income],
    } as IncomeStream);
    const columnOriginal = getFirstValue(income, [
      originalAmountKey,
      `metadata.${originalAmountKey}`,
      `ocrResult.${originalAmountKey}`,
      `ocrResult.metadata.${originalAmountKey}`,
    ]);
    const fields = {
      ...resolved,
      originalAmount: hasInternationalField(columnOriginal)
        ? columnOriginal
        : resolved.originalAmount,
      originalAnnualAmount: undefined,
    };
    const showDetails = shouldCaptionAsConverted({
      isManagerOverride: hasOverride(
        _.get(annotationForIncome(income), annotationKey)
      ),
      convertedAmount,
      streamQualifies: shouldShowConversionDetails(
        showConversionBreakdown,
        streamVerificationMethod,
        fields
      ),
    });
    return showDetails ? (
      <ConvertedAmount fields={fields}>{amount}</ConvertedAmount>
    ) : (
      amount
    );
  };

  const primaryOcrResultId = _.chain(incomes)
    .map((income) => _.get(income, "sourceOcrResult"))
    .compact()
    .first()
    .value();
  const { ocrResult: streamOcrResult, isLoading: isPrimaryOcrLoading } =
    useGetOcrResultById(primaryOcrResultId || "");
  const expectFraudCard = streamHasFraud(incomeStream);

  const [rowOcrResults, setRowOcrResults] = useState<Record<string, any>>({});
  const handleRowOcrLoaded = useCallback((id: string, ocrResult: any) => {
    setRowOcrResults((prev) =>
      prev[id] === ocrResult ? prev : { ...prev, [id]: ocrResult }
    );
  }, []);
  const siblingOcrResultIds = _.chain(incomes)
    .map((income) => _.get(income, "sourceOcrResult"))
    .compact()
    .uniq()
    .filter((id) => id !== primaryOcrResultId)
    .value();
  const canSeeFraud = isManagerApp() && canViewOcrResults;
  const allOcrResults = _.compact([
    streamOcrResult,
    ..._.map(siblingOcrResultIds, (id) => rowOcrResults[id]),
  ]);
  const allSiblingsLoaded = _.every(
    siblingOcrResultIds,
    (id) => !!rowOcrResults[id]
  );
  const primaryReady =
    !primaryOcrResultId || (!isPrimaryOcrLoading && !!streamOcrResult);
  const allOcrLoaded = primaryReady && allSiblingsLoaded;
  const isSingleDocumentStream = detectSingleDocumentStream({
    ocrResults: allOcrResults,
    incomesCount: incomes?.length ?? 0,
    canSeeFraud,
  });

  const ocrResultForIncome = (income: any): any => {
    const sourceOcrResult = _.get(income, "sourceOcrResult");
    if (!sourceOcrResult) return null;
    const matchesPrimary =
      sourceOcrResult === _.get(streamOcrResult, "id") ||
      sourceOcrResult === _.get(streamOcrResult, "ocrResultId");
    if (matchesPrimary) return streamOcrResult;
    return rowOcrResults[sourceOcrResult] || null;
  };
  const annotationForIncome = (income: any) =>
    _.get(ocrResultForIncome(income), "annotation") || {};
  const hasOverride = (v: any) => v !== null && v !== undefined && v !== "";
  const isRowAnnotated = (income: any) =>
    _.some(_.values(annotationForIncome(income)), hasOverride);
  const getAnnotatedGross = (income: any) => {
    const override = _.get(annotationForIncome(income), "grossAmount");
    return hasOverride(override)
      ? convertDollarsToCents(Number(override))
      : income?.grossAmount;
  };
  const getAnnotatedNet = (income: any) => {
    const override = _.get(annotationForIncome(income), "netAmount");
    return hasOverride(override)
      ? convertDollarsToCents(Number(override))
      : income?.netAmount;
  };

  const renderEditModal = (stream) => {
    const annotation = annotationForIncome(stream);
    const enrichedStream = {
      ...stream,
      ...(hasOverride(annotation.payer) && { description: annotation.payer }),
      ...(hasOverride(annotation.payPeriodStartDate) && {
        periodStart: annotation.payPeriodStartDate,
      }),
      ...(hasOverride(annotation.payPeriodEndDate) && {
        periodEnd: annotation.payPeriodEndDate,
      }),
      ...(hasOverride(annotation.payDate) && {
        dateReceived: annotation.payDate,
      }),
      ...(hasOverride(annotation.grossAmount) && {
        grossAmount: convertDollarsToCents(Number(annotation.grossAmount)),
      }),
      ...(hasOverride(annotation.netAmount) && {
        netAmount: convertDollarsToCents(Number(annotation.netAmount)),
      }),
      ...(hasOverride(annotation.formYear) && {
        year: Number(annotation.formYear),
      }),
    };
    const originalValues = {
      description: stream.description,
      periodStart: stream.periodStart,
      periodEnd: stream.periodEnd,
      dateReceived: stream.dateReceived,
      grossAmount: stream.grossAmount,
      netAmount: stream.netAmount,
      year: stream.year,
    };
    return openModalDialog(EditStreamDetailModal, {
      stream: enrichedStream,
      originalValues,
      submit: ({ id, data }) => {
        patchOcrResultById({ id, data });
      },
    });
  };

  const columns = [
    {
      title: <IncomeStreamHeader>Type</IncomeStreamHeader>,
      dataIndex: "type",
      render: (_, { incomeVerificationMethod, source }) => (
        <RegularText>
          {mapIncomeStreamVerificationMethodToType(
            incomeVerificationMethod,
            source
          )}
        </RegularText>
      ),
    },
    ...(renderBankAccount
      ? [
          {
            title: <IncomeStreamHeader>Bank Account</IncomeStreamHeader>,
            dataIndex: "bankAccount",
            render: (__, income) => (
              <RegularText>{getIncomeBankAccount(income)}</RegularText>
            ),
          },
        ]
      : []),
    {
      title: (
        <IncomeStreamHeader>
          {isTaxFormStream
            ? "Tax Year"
            : isEmploymentLetterStream
            ? "Letter Date"
            : "Payment Date"}
        </IncomeStreamHeader>
      ),
      // eslint-disable-next-line prettier/prettier
      dataIndex: "paymentDate",
      render: (_, { incomeVerificationMethod, dateReceived, year }) => (
        <RegularText>
          {isTaxFormStream ||
          ["ocr-1099", "ocr-w2", "ocr-international-tax-form"].includes(
            incomeVerificationMethod
          )
            ? year
            : dateReceived
            ? dayjs(dateReceived).format("MM/DD/YYYY")
            : "N/A"}
        </RegularText>
      ),
    },
    {
      title: <IncomeStreamHeader>Gross Amount</IncomeStreamHeader>,
      dataIndex: "grossAmount",
      render: (_, income) =>
        withConversionDetails(
          income,
          "grossAmount",
          "originalGrossAmount",
          getAnnotatedGross(income),
          <RegularText>
            {renderAmountCell(getAnnotatedGross(income))}
          </RegularText>
        ),
    },
    {
      title: <IncomeStreamHeader>Net Amount</IncomeStreamHeader>,
      dataIndex: "netAmmount",
      render: (_, income) =>
        withConversionDetails(
          income,
          "netAmount",
          "originalNetAmount",
          getAnnotatedNet(income),
          <RegularText>{renderAmountCell(getAnnotatedNet(income))}</RegularText>
        ),
    },
    ...(canEditOcrResults ||
    (!dealDecisionIsMade && hasAnyDocumentUrl && !shouldRedactDocuments)
      ? [
          {
            title: "",
            dataIndex: "actions",
            render: (__, stream) => (
              <ActionsContainer>
                {canEditOcrResults && isRowAnnotated(stream) && (
                  <EditedChip label="EDITED" color="light-grey" />
                )}
                {(() => {
                  if (
                    dealDecisionIsMade ||
                    !stream?.documentUrl ||
                    shouldRedactDocuments
                  )
                    return null;
                  const topLevelFraudCardShowing =
                    isSingleDocumentStream &&
                    canSeeFraud &&
                    ocrResultHasVisibleFraud(streamOcrResult);
                  if (topLevelFraudCardShowing) return null;
                  const rowOcr = ocrResultForIncome(stream);
                  if (rowOcr?.provider === "vega") return null;
                  return (
                    <DocumentLink
                      onClick={(e) => e.stopPropagation()}
                      href={stream?.documentUrl}
                      target="_blank"
                      type="link"
                    >
                      View
                    </DocumentLink>
                  );
                })()}
                {canEditOcrResults &&
                  stream?.sourceOcrResult &&
                  editableStreamVerificationMethodType(
                    stream?.incomeVerificationMethod,
                    stream?.source
                  ) &&
                  !isViewOnly &&
                  (() => {
                    const rowOcrLoaded = !!ocrResultForIncome(stream);
                    const tooltipTitle = dealDecisionIsMade
                      ? "Income stream values cannot be modified once a deal has been decisioned"
                      : !rowOcrLoaded
                      ? "Loading…"
                      : "";
                    return (
                      <StyledTooltip
                        getPopupContainer={(triggerNode) => triggerNode}
                        placement="topRight"
                        title={tooltipTitle}
                      >
                        <div>
                          <IconLink
                            disabled={dealDecisionIsMade || !rowOcrLoaded}
                            Icon={Icon.EditPencilLargeIcon}
                            // @ts-ignore
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!rowOcrLoaded) return;
                              renderEditModal(stream);
                            }}
                          />
                        </div>
                      </StyledTooltip>
                    );
                  })()}
              </ActionsContainer>
            ),
          },
        ]
      : []),
  ];

  const renderExpandedRow = (income: any) => {
    if (isSingleDocumentStream) return null;
    if (!canSeeFraud) return null;
    if (!incomeIsFlaggedForFraud(income)) return null;
    if (!allOcrLoaded) {
      return (
        <FraudSignalContainer>
          <Icon.ArrowEnter />
          <FraudCardSkeleton compact />
        </FraudSignalContainer>
      );
    }
    const ocrResult = ocrResultForIncome(income);
    if (!ocrResult || !ocrResultHasVisibleFraud(ocrResult)) return null;
    return (
      <FraudSignalContainer>
        <Icon.ArrowEnter />
        <FraudCard
          ocrResult={ocrResult}
          dealDecisionIsMade={dealDecisionIsMade}
          compact
          onOpenFraudViz={(r) =>
            openModalDialog(FraudSignalsIframeModal, {
              documentId: r.ocrResultId,
              fraudData: [r],
            })
          }
        />
      </FraudSignalContainer>
    );
  };

  const ocrResultWithSignals =
    _.find(
      allOcrResults,
      (r) =>
        !_.isEmpty(filterSuppressedFraudSignals(_.get(r, "fraudSignals") || []))
    ) || streamOcrResult;
  const showSingleDocFraudCard =
    allOcrLoaded &&
    isSingleDocumentStream &&
    canSeeFraud &&
    ocrResultHasVisibleFraud(streamOcrResult);
  const showSingleDocFraudSkeleton =
    !allOcrLoaded &&
    canSeeFraud &&
    expectFraudCard &&
    (incomes?.length ?? 0) > 1;
  const singleDocFraudCard = showSingleDocFraudCard ? (
    <FraudCard
      ocrResult={ocrResultWithSignals}
      dealDecisionIsMade={dealDecisionIsMade}
      onOpenFraudViz={(r) =>
        openModalDialog(FraudSignalsIframeModal, {
          documentId: r.ocrResultId,
          fraudData: [r],
        })
      }
    />
  ) : showSingleDocFraudSkeleton ? (
    <FraudCardSkeleton />
  ) : null;

  return (
    <StreamDetailContainer>
      {canSeeFraud &&
        siblingOcrResultIds.map((id) => (
          <OcrRowPrefetcher
            key={id}
            sourceOcrResult={id}
            onLoaded={handleRowOcrLoaded}
          />
        ))}
      {singleDocFraudCard}
      {!isEmpty(incomes) && (
        <IncomeStreamDetailTable
          columns={columns}
          dataSource={incomes}
          // @ts-ignore
          expandedRowClassName={() => "income-ocr-expanded"}
          expandedRowRender={renderExpandedRow}
          expandIconAsCell={false}
          expandIconColumnIndex={columns.length - 1}
          expandRowByClick
          style={{ cursor: "pointer" }}
          expandIcon={({ record, expanded, onExpand }: any) => {
            if (isSingleDocumentStream) return null;
            const ocrResult = ocrResultForIncome(record);
            if (
              !ocrResult ||
              !canSeeFraud ||
              !ocrResultHasVisibleFraud(ocrResult)
            )
              return null;
            return (
              <CollapseButton
                collapsed={!expanded}
                collapsibleOnDesktop
                collapsibleOnMobile
                onClick={(e: any) => onExpand(record, e)}
                style={{
                  float: "right",
                  display: "inline-flex",
                  alignItems: "center",
                }}
              >
                <Icon.ChevronDownIcon />
              </CollapseButton>
            );
          }}
          defaultExpandAllRows
          noBorderBottom
          noClickableRows
          pagination={false}
          rowKey={({ id }) => id}
          size="small"
        />
      )}
    </StreamDetailContainer>
  );
};
